import * as React from 'react';
import { History } from 'history';
import { Link } from 'react-router-dom';
import {
  createSuperAdminUser,
  setSuperAdminUserEmailVerification,
  setSuperAdminUserSmsVerification,
  SuperAdminUserCreateData
} from 'Src/helpers/service/superadmin/users';

interface Props {
  history: History;
}

interface UserDraft {
  firstName: string;
  lastName: string;
  gender: string;
  birthday: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  misEmail: string;
  misPhone: string;
  isPaymentsEnabled: boolean;
  emailVerified: boolean;
  smsVerified: boolean;
  countOneTimeIosSessions: string;
  countOneTimeAndroidSessions: string;
  countOneTimeWebSessions: string;
}

interface State {
  draft: UserDraft;
  isSaving: boolean;
  errorMessage: string;
  createdUserId: string;
}

function getDefaultDraft(): UserDraft {
  return {
    firstName: '',
    lastName: '',
    gender: '',
    birthday: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    misEmail: '',
    misPhone: '',
    isPaymentsEnabled: false,
    emailVerified: true,
    smsVerified: true,
    countOneTimeIosSessions: '',
    countOneTimeAndroidSessions: '',
    countOneTimeWebSessions: ''
  };
}

function getOptionalNumber(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

function getUserId(user: { id?: string; _id?: string }): string {
  return user.id || user._id || '';
}

export class SuperAdminUserForm extends React.Component<Props, State> {
  state: State = {
    draft: getDefaultDraft(),
    isSaving: false,
    errorMessage: '',
    createdUserId: ''
  };

  onTextChange = (field: keyof UserDraft) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const value = event.target.value;
    this.setState(prevState => ({ draft: { ...prevState.draft, [field]: value } }));
  };

  onCheckboxChange = (field: keyof UserDraft) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked;
    this.setState(prevState => ({ draft: { ...prevState.draft, [field]: checked } }));
  };

  validate(): string {
    const { draft } = this.state;
    if (!draft.firstName.trim()) return 'First name is required.';
    if (!draft.lastName.trim()) return 'Last name is required.';
    if (!draft.gender) return 'Gender is required.';
    if (!draft.email.trim()) return 'Email is required.';
    if (!draft.phone.trim()) return 'Phone is required.';
    if (!draft.password) return 'Password is required.';
    if (draft.password !== draft.confirmPassword) return 'Passwords do not match.';

    for (const [label, value] of [
      ['One-time iOS sessions', draft.countOneTimeIosSessions],
      ['One-time Android sessions', draft.countOneTimeAndroidSessions],
      ['One-time Web sessions', draft.countOneTimeWebSessions]
    ]) {
      if (value.trim() !== '' && (!Number.isInteger(Number(value)) || Number(value) < 0)) {
        return `${label} must be a non-negative whole number.`;
      }
    }
    return '';
  }

  onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const validationError = this.validate();
    if (validationError) {
      this.setState({ errorMessage: validationError });
      return;
    }

    const { draft } = this.state;
    const data: SuperAdminUserCreateData = {
      firstName: draft.firstName.trim(),
      lastName: draft.lastName.trim(),
      gender: draft.gender,
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      password: draft.password,
      birthday: draft.birthday || null,
      misEmail: draft.misEmail.trim(),
      misPhone: draft.misPhone.trim(),
      isPaymentsEnabled: draft.isPaymentsEnabled,
      countOneTimeIosSessions: getOptionalNumber(draft.countOneTimeIosSessions),
      countOneTimeAndroidSessions: getOptionalNumber(draft.countOneTimeAndroidSessions),
      countOneTimeWebSessions: getOptionalNumber(draft.countOneTimeWebSessions)
    };

    this.setState({ isSaving: true, errorMessage: '', createdUserId: '' });
    createSuperAdminUser(data)
      .then(user => {
        const userId = getUserId(user);
        if (!userId) throw new Error('Created user response has no id.');
        this.setState({ createdUserId: userId });
        return Promise.all([
          draft.emailVerified ? setSuperAdminUserEmailVerification(userId, true) : Promise.resolve(user),
          draft.smsVerified ? setSuperAdminUserSmsVerification(userId, true) : Promise.resolve(user)
        ]).then(() => userId);
      })
      .then(userId => this.props.history.push(`/users/${userId}`))
      .catch(error => {
        console.error('Can not create superadmin user', error);
        this.setState(prevState => ({
          isSaving: false,
          errorMessage: prevState.createdUserId
            ? 'The user was created, but verification flags could not be updated. Open the user and review verification.'
            : 'Can not create user. Check that email and phone are unique and all fields are valid.'
        }));
      });
  };

  renderNumberField(label: string, field: keyof UserDraft): React.ReactNode {
    const { draft, isSaving } = this.state;
    return (
      <div className="form-group col-md-4">
        <label>{label}</label>
        <input
          type="number"
          min="0"
          step="1"
          className="form-control"
          value={String(draft[field])}
          onChange={this.onTextChange(field)}
          disabled={isSaving}
        />
      </div>
    );
  }

  render() {
    const { draft, isSaving, errorMessage, createdUserId } = this.state;
    return (
      <div className="container-fluid mt-3">
        <div className="d-flex align-items-center mb-3">
          <Link to="/users" className="btn btn-secondary btn-sm mr-3">
            Back to users
          </Link>
          <h3 className="mb-0">Create new user</h3>
        </div>

        {errorMessage && (
          <div className="alert alert-danger">
            {errorMessage} {createdUserId && <Link to={`/users/${createdUserId}`}>Open created user</Link>}
          </div>
        )}

        <form onSubmit={this.onSubmit}>
          <div className="card mb-3">
            <div className="card-header">
              <strong>Account details</strong>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>First name *</label>
                  <input
                    className="form-control"
                    value={draft.firstName}
                    onChange={this.onTextChange('firstName')}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>Last name *</label>
                  <input
                    className="form-control"
                    value={draft.lastName}
                    onChange={this.onTextChange('lastName')}
                    disabled={isSaving}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-4">
                  <label>Gender *</label>
                  <select
                    className="form-control"
                    value={draft.gender}
                    onChange={this.onTextChange('gender')}
                    disabled={isSaving}
                  >
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </div>
                <div className="form-group col-md-4">
                  <label>Birthday</label>
                  <input
                    type="date"
                    className="form-control"
                    value={draft.birthday}
                    onChange={this.onTextChange('birthday')}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={draft.phone}
                    onChange={this.onTextChange('phone')}
                    disabled={isSaving}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={draft.email}
                    onChange={this.onTextChange('email')}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>Password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={draft.password}
                    onChange={this.onTextChange('password')}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>Confirm password *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={draft.confirmPassword}
                    onChange={this.onTextChange('confirmPassword')}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <strong>MIS delivery contacts</strong>
            </div>
            <div className="card-body">
              <div className="alert alert-info py-2">
                MIS IDs belong to permissions. Create the account first, then add its school role on the Permissions
                tab.
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>MIS email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={draft.misEmail}
                    onChange={this.onTextChange('misEmail')}
                    disabled={isSaving}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>MIS phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={draft.misPhone}
                    onChange={this.onTextChange('misPhone')}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header">
              <strong>Access and verification</strong>
            </div>
            <div className="card-body">
              <div className="form-row">
                {this.renderNumberField('One-time iOS sessions', 'countOneTimeIosSessions')}
                {this.renderNumberField('One-time Android sessions', 'countOneTimeAndroidSessions')}
                {this.renderNumberField('One-time Web sessions', 'countOneTimeWebSessions')}
              </div>
              <div className="form-check mb-2">
                <input
                  id="create-user-payments"
                  type="checkbox"
                  className="form-check-input"
                  checked={draft.isPaymentsEnabled}
                  onChange={this.onCheckboxChange('isPaymentsEnabled')}
                  disabled={isSaving}
                />
                <label htmlFor="create-user-payments" className="form-check-label">
                  Payments enabled
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  id="create-user-email-verified"
                  type="checkbox"
                  className="form-check-input"
                  checked={draft.emailVerified}
                  onChange={this.onCheckboxChange('emailVerified')}
                  disabled={isSaving}
                />
                <label htmlFor="create-user-email-verified" className="form-check-label">
                  Mark email as verified
                </label>
              </div>
              <div className="form-check">
                <input
                  id="create-user-sms-verified"
                  type="checkbox"
                  className="form-check-input"
                  checked={draft.smsVerified}
                  onChange={this.onCheckboxChange('smsVerified')}
                  disabled={isSaving}
                />
                <label htmlFor="create-user-sms-verified" className="form-check-label">
                  Mark phone as verified
                </label>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary mr-2" disabled={isSaving}>
            {isSaving ? 'Creating...' : 'Create user'}
          </button>
          <Link to="/users" className={`btn btn-secondary${isSaving ? ' disabled' : ''}`}>
            Cancel
          </Link>
        </form>
      </div>
    );
  }
}
